import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { BookOneLesson } from "../booking/BookOneLesson.js";
import { formatDate, formatTime } from "../format.js";
import { Drawer } from "../ui/Dialog.js";
import type { DiaryEntry } from "./entries.js";
import {
  readBookingSuggestionEntries,
  suggestStudents,
  type BookingSuggestionRange,
} from "./bookingSuggestions.js";

export interface BookLessonHint {
  startsAt: string;
  tutorId?: string;
  /** Quarter-hour choices inside the selected schedule block, when present. */
  startsAtOptions?: readonly string[];
  blockReason?: string;
}

export function BookLessonDrawer({
  hint,
  onClose,
  onBooked,
  inline = false,
  suggestionEntries,
  suggestionRange = null,
  studentSearchRef: providedStudentSearchRef,
  onMutationBusyChange,
}: {
  hint: BookLessonHint;
  onClose: () => void;
  onBooked: () => void;
  /** Render the booking controls inside another drawer surface. */
  inline?: boolean;
  /** Present for empty-slot booking, where Diary entries can seed suggestions. */
  suggestionEntries?: readonly DiaryEntry[];
  /** Missing history interval, or null when the loaded Diary already covers it. */
  suggestionRange?: BookingSuggestionRange | null;
  studentSearchRef?: RefObject<HTMLInputElement>;
  onMutationBusyChange?: (busy: boolean) => void;
}) {
  const [mutationBusy, setMutationBusy] = useState(false);
  const [startsAt, setStartsAt] = useState(hint.startsAt);
  const [suggestedStudents, setSuggestedStudents] = useState<{ studentId: string; name: string }[]>([]);
  const ownStudentSearchRef = useRef<HTMLInputElement>(null);
  const studentSearchRef = providedStudentSearchRef ?? ownStudentSearchRef;
  const startsAtOptions = hint.startsAtOptions ?? [];

  useEffect(() => {
    setStartsAt(hint.startsAt);
  }, [hint.startsAt]);

  useEffect(() => {
    let current = true;
    setSuggestedStudents([]);
    if (suggestionEntries === undefined) return () => { current = false; };

    const update = (fetched: readonly DiaryEntry[] | null) => {
      if (!current) return;
      setSuggestedStudents(suggestStudents(
        [...suggestionEntries, ...(fetched ?? [])],
        { startsAt, tutorId: hint.tutorId ?? null },
      ));
    };

    if (suggestionRange === null) {
      update([]);
    } else {
      void readBookingSuggestionEntries(suggestionRange).then(update);
    }
    return () => { current = false; };
  }, [hint.tutorId, startsAt, suggestionEntries, suggestionRange]);

  const handleMutationBusyChange = useCallback((busy: boolean) => {
    setMutationBusy(busy);
    onMutationBusyChange?.(busy);
  }, [onMutationBusyChange]);

  const bookingContent = (
    <div className="cal-booking-drawer-body">
      {startsAtOptions.length > 0 && (
        <label className="stack" style={{ marginBottom: 16 }}>
          <span className="small muted">Exact time over block</span>
          <select
            aria-label="Exact time over block"
            disabled={mutationBusy}
            value={startsAt}
            onChange={(event) => setStartsAt(event.target.value)}
          >
            {startsAtOptions.map((option) => (
              <option key={option} value={option}>
                {formatTime(option)}
              </option>
            ))}
          </select>
        </label>
      )}
      <BookOneLesson
        showLessonHeading={false}
        key={`${hint.tutorId ?? "choose"}:${startsAt}`}
        initialTutorId={hint.tutorId ?? ""}
        initialStartsAt={startsAt}
        suggestedStudents={suggestedStudents}
        studentSearchRef={studentSearchRef}
        onBooked={onBooked}
        onMutationBusyChange={handleMutationBusyChange}
      />
    </div>
  );

  if (inline) return bookingContent;

  return (
    <Drawer
      eyebrow="Diary"
      title="Book lesson"
      description={(
        <>
          {formatDate(startsAt)} · {formatTime(startsAt)} suggested by the diary
          {hint.blockReason ? ` · over ${hint.blockReason}` : ""}
        </>
      )}
      onClose={onClose}
      busy={mutationBusy}
      closeLabel="Close book lesson"
      className="cal-lesson-drawer cal-booking-drawer"
      size="wide"
      initialFocusRef={studentSearchRef}
    >
      {bookingContent}
    </Drawer>
  );
}
